import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useTranslation } from '../../../contexts/TranslationContext';
import { supabase } from '../../../lib/supabase';

const PasswordTab: React.FC = () => {
  const theme = useTheme();
  const { t, language } = useTranslation();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error(t('settings.password.error.mismatch'));
      return;
    }

    if (formData.newPassword.length < 8) {
      toast.error(t('settings.password.error.minlength'));
      return;
    }

    setLoading(true);
    try {
      // Get current user to retrieve their email
      const { data: userData, error: userErr } = await supabase.auth.getUser();
      if (userErr || !userData?.user) {
        throw new Error('Not authenticated');
      }
      const email = userData.user.email || '';
      if (!email) throw new Error('Missing user email');

      // Verify current password by re-authenticating
      const { error: verifyErr } = await supabase.auth.signInWithPassword({
        email,
        password: formData.currentPassword,
      });
      if (verifyErr) {
        toast.error('Invalid current password');
        return;
      }

      // Update password
      const { error: updateErr } = await supabase.auth.updateUser({ password: formData.newPassword });
      if (updateErr) throw updateErr;

      toast.success(t('settings.password.success.updated'));
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });

      // Best-effort: notify user via email using default Mailjet connection through Edge Function
      try {
        await supabase.functions.invoke('send-email', {
          body: {
            recipients: [{ email }],
            // Use platform message template if available and active
            eventKey: 'auth.password_changed',
            locale: language,
            // Fallbacks if no template is configured
            subject: 'Your password was changed',
            text: 'Hi,\n\nYour account password was just changed. If this was not you, please contact the administrator immediately.\n\nBest regards,\nSupport',
          }
        });
      } catch (notifyErr) {
        // Do not block UX on email failure; optional soft notice
        console.warn('Password change email notification failed', notifyErr);
      }
    } catch (e: any) {
      const msg = e?.message || 'Failed to update password';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const passwordRequirements = [
    { text: t('settings.password.requirements.minLength'), met: formData.newPassword.length >= 8 },
    { text: t('settings.password.requirements.upper'), met: /[A-Z]/.test(formData.newPassword) },
    { text: t('settings.password.requirements.lower'), met: /[a-z]/.test(formData.newPassword) },
    { text: t('settings.password.requirements.number'), met: /\d/.test(formData.newPassword) },
    { text: t('settings.password.requirements.special'), met: /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword) },
  ];

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
            {t('settings.password.title')}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {t('settings.password.subtitle')}
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            border: '1px solid rgba(0, 0, 0, 0.05)',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.6) 100%)',
          }}
        >
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t('settings.password.current')}
              type={showCurrentPassword ? 'text' : 'password'}
              value={formData.currentPassword}
              onChange={handleInputChange('currentPassword')}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <TextField
              fullWidth
              label={t('settings.password.new')}
              type={showNewPassword ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={handleInputChange('newPassword')}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <TextField
              fullWidth
              label={t('settings.password.confirm')}
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleInputChange('confirmPassword')}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            {/* Password Requirements */}
            {formData.newPassword && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('settings.password.requirements.title')}
                </Typography>
                {passwordRequirements.map((req, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <CheckIcon
                      sx={{
                        color: req.met ? 'success.main' : 'text.disabled',
                        fontSize: 20,
                        mr: 1,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: req.met ? 'success.main' : 'text.secondary',
                        textDecoration: req.met ? 'none' : 'line-through',
                      }}
                    >
                      {req.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                },
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              {loading ? t('settings.password.button.updating') : t('settings.password.button.update')}
            </Button>
          </form>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default PasswordTab;
